import type { ReactNode } from 'react';
import type {
  LegalDocument,
  LegalFlow,
  LegalInline,
} from '../../legal/parseLegalDocument';

function isEmail(value: string): boolean {
  return /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/.test(value);
}

function renderInlines(nodes: LegalInline[]): ReactNode[] {
  return nodes.map((node, i) => {
    const key = `${node.type}-${i}`;
    if (node.type === 'text') return <span key={key}>{node.value}</span>;
    if (node.type === 'break') return <br key={key} />;
    if (node.type === 'code') return <code key={key}>{node.value}</code>;
    if (node.type === 'link') {
      return (
        <a key={key} href={node.href}>
          {node.value}
        </a>
      );
    }
    if (isEmail(node.value)) {
      return (
        <strong key={key}>
          <a href={`mailto:${node.value}`}>{node.value}</a>
        </strong>
      );
    }
    return <strong key={key}>{node.value}</strong>;
  });
}

function renderFlow(block: LegalFlow, key: string): ReactNode {
  if (block.type === 'p') {
    return <p key={key}>{renderInlines(block.children)}</p>;
  }
  if (block.type === 'ul') {
    return (
      <ul key={key}>
        {block.items.map((item, i) => (
          <li key={`${key}-item-${i}`}>{renderInlines(item)}</li>
        ))}
      </ul>
    );
  }
  return (
    <div key={key} data-legal-table>
      <table>
        <thead>
          <tr>
            {block.headers.map((header) => (
              <th key={header}>{header}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          {block.rows.map((row) => (
            <tr key={row.join('|')}>
              {row.map((cell, i) => (
                <td key={`${row.join('|')}-${i}`}>{cell}</td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

export function LegalMarkdown({ doc }: { doc: LegalDocument }) {
  return (
    <>
      <header>
        <h1>{doc.title}</h1>
        {doc.updatedText ? (
          <p>
            Last updated:{' '}
            <time dateTime={doc.updatedIso}>{doc.updatedText}</time>
          </p>
        ) : null}
      </header>
      {doc.intro.map((block, i) => renderFlow(block, `intro-${i}`))}
      {doc.sections.map((section, i) => (
        <section key={`section-${i}`}>
          <h2>{renderInlines(section.heading)}</h2>
          {section.blocks.map((block, j) =>
            renderFlow(block, `section-${i}-${j}`),
          )}
        </section>
      ))}
    </>
  );
}
